import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useRealtimeStore } from "../../lib/store.js";

// Через сколько после получения outcome автоматически закрываем модалку.
// 4 секунды — достаточно, чтобы юзер успел прочитать результат, но не тормозит.
const AUTO_DISMISS_MS = 4000;

const outcomeLabel = (
  outcome: "picked_up" | "discarded" | "timeout" | "failed",
  failureMessage?: string
): { icon: string; tone: "ok" | "warn"; text: string } => {
  switch (outcome) {
    case "picked_up":
      return { icon: "ic-checkround", tone: "ok", text: "Абонент принял (сразу сбросили)" };
    case "discarded":
      return { icon: "ic-restrict", tone: "warn", text: "Абонент сбросил вызов" };
    case "timeout":
      return { icon: "ic-clock", tone: "warn", text: "Абонент не ответил" };
    case "failed":
      return {
        icon: "ic-warning",
        tone: "warn",
        text: failureMessage ? `Ошибка: ${failureMessage}` : "Ошибка вызова"
      };
  }
};

export const PendingCallModal = () => {
  const pendingCall = useRealtimeStore((state) => state.pendingCall);
  const dismiss = useRealtimeStore((state) => state.dismissPendingCall);

  // Автозакрытие после получения outcome. Пока ringing — не закрываем,
  // ждём событие от worker'а (но воркер сам тайм-аутит за ~8s).
  useEffect(() => {
    if (!pendingCall?.outcome) {
      return;
    }
    const handle = window.setTimeout(dismiss, AUTO_DISMISS_MS);
    return () => window.clearTimeout(handle);
  }, [pendingCall?.outcome, pendingCall?.finishedAt, dismiss]);

  // Esc закрывает модалку.
  useEffect(() => {
    if (!pendingCall) {
      return;
    }
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        dismiss();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pendingCall, dismiss]);

  if (!pendingCall) {
    return null;
  }

  const outcome = pendingCall.outcome
    ? outcomeLabel(pendingCall.outcome, pendingCall.failureMessage)
    : null;

  return createPortal(
    <div
      className="pending-call"
      role="dialog"
      aria-modal="true"
      aria-live="polite"
      aria-label="Состояние звонка"
    >
      <button
        type="button"
        className="pending-call__backdrop"
        onClick={dismiss}
        aria-label="Закрыть"
      />
      <div className="pending-call__panel">
        <div className="pending-call__title">{pendingCall.chatTitle}</div>
        {outcome ? (
          <div className="pending-call__outcome">
            <i
              className={`ic ${outcome.icon} pending-call__outcome-icon pending-call__outcome-icon--${outcome.tone}`}
              aria-hidden="true"
            />
            <span>{outcome.text}</span>
          </div>
        ) : (
          <div className="pending-call__progress">
            <span className="pending-call__spinner" aria-hidden="true" />
            <span>
              {pendingCall.ringing
                ? "Гудки идут у абонента…"
                : "Соединение с Telegram…"}
            </span>
          </div>
        )}
        <button
          type="button"
          className="pending-call__close button button--ghost"
          onClick={dismiss}
        >
          Закрыть
        </button>
      </div>
    </div>,
    document.body
  );
};
